import React, { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { Pencil, Trash2, Check } from 'lucide-react'
import axios from '../../../../config/axios'
import { usePipelineContext } from '../PipelineContext'
import * as S from './style'
import styled from 'styled-components'

const SwitchToggle = styled.div<{ active?: boolean }>`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background-color: ${props => props.active ? '#0090D3' : '#E5E5E5'};
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  position: relative;
`

const SwitchThumb = styled.div<{ active?: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  position: absolute;
  left: ${props => props.active ? '22px' : '2px'};
  transition: all 0.2s ease;
  color: #0090D3;
`

interface Automation {
  id: number
  name: string
  description: string
  isActive: boolean
  triggerTable: string
  triggerEvent: string
  triggerColumn?: string
  conditions?: any
}

const getEventDisplayName = (automation: Automation): string => {
  const { triggerTable, triggerEvent, triggerColumn } = automation

  if (automation.conditions?.markerConfig) {
    return 'Marcador'
  }
  
  if (triggerTable === 'Ticket' && triggerEvent === 'INSERT') {
    return 'Card é criado'
  }
  
  if (triggerTable === 'Ticket' && triggerEvent === 'UPDATE' && triggerColumn === 'stepId') {
    return 'Card entra na etapa'
  }
  
  if (triggerTable === 'TicketFieldValue' && triggerEvent === 'UPDATE') {
    return 'Campo é atualizado'
  }
  
  if (triggerTable === 'TicketFieldValue' && triggerEvent === 'INSERT') {
    return 'Campo é preenchido'
  }
  
  return `${triggerTable} - ${triggerEvent}`
}

const Breadcrumbs: React.FC<{ items: { label: string; path?: string }[] }> = ({
  items,
}) => (
  <S.BreadcrumbsContainer>
    <ol>
      {items.map((item, index) => (
        <li key={index}>
          {index === items.length - 1 ? (
            <span>{item.label}</span>
          ) : item.path ? (
            <Link to={item.path}>{item.label}</Link>
          ) : (
            <span style={{color: '#0090d3', cursor: 'pointer'}}>{item.label}</span>
          )}
        </li>
      ))}
    </ol>
  </S.BreadcrumbsContainer>
)

const AutomationsPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const pipelineId = searchParams.get('pipelineId')
  const { activeAutomationTab, pipelineName, tenantId, setIsCreatingAutomation, setEditingAutomationId } = usePipelineContext()
  const [automations, setAutomations] = useState<Automation[]>([])
  const [stepNameById, setStepNameById] = useState<Record<number, string>>({})
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (tenantId) {
      fetchAutomations()
    }
  }, [pipelineId, tenantId])

  const fetchAutomations = async () => {
    if (!tenantId || !pipelineId) return
    setIsLoading(true)
    try {
      const relationsResponse = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline/${pipelineId}/automation`
      )

      const stepsResponse = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step?pipelineId=${pipelineId}`
      )

      const mappedSteps = (stepsResponse.data?.data || []).reduce((acc: Record<number, string>, step: any) => {
        if (step?.id && step?.name) {
          acc[Number(step.id)] = String(step.name)
        }
        return acc
      }, {})
      setStepNameById(mappedSteps)
      
      const automationIds = relationsResponse.data?.data?.map((r: any) => r.automationId) || []
      
      if (automationIds.length === 0) {
        setAutomations([])
        return
      }
      
      const automationsResponse = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/automation-service/automation?tenantId=${tenantId}&module=pipelines`
      )
      
      const allAutomations = Array.isArray(automationsResponse.data) ? automationsResponse.data : []
      const filteredAutomations = allAutomations.filter((a: Automation) => automationIds.includes(a.id))
      
      setAutomations(filteredAutomations)
    } catch (error) {
      console.error(error)
      setAutomations([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleToggleActive = async (id: number, currentStatus: boolean) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/automation-service/automation/${id}`,
        { isActive: !currentStatus }
      )
      setAutomations(prev => 
        prev.map(a => a.id === id ? { ...a, isActive: !currentStatus } : a)
      )
    } catch (error) {
      console.error(error)
    }
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir esta automação?')) return
    
    try {
      await axios.delete(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline/${pipelineId}/automation/${id}`
      )
      await axios.delete(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/automation-service/automation/${id}`
      )
      setAutomations(prev => prev.filter(a => a.id !== id))
    } catch (error) {
      console.error(error)
    }
  }

  const handleEdit = (id: number) => {
    setEditingAutomationId(id)
    setIsCreatingAutomation(true)
  }

  const formatAutomationDescription = (description?: string) => {
    if (!description) return '-'
    return description.replace(/etapa\s+(\d+)/gi, (match, stepId) => {
      const resolvedStepName = stepNameById[Number(stepId)]
      if (!resolvedStepName) {
        return match
      }
      return `etapa "${resolvedStepName}"`
    })
  }

  const breadcrumbItems = [
    { label: 'Funis', path: '/panel/pipelines' },
    { label: pipelineName || 'Nome do funil' },
    { label: 'Automação' },
  ]

  return (
    <S.Container>
      <Breadcrumbs items={breadcrumbItems} />
      <S.Content>
        {activeAutomationTab === 'automations' && (
          <S.Table>
            <S.TableHeader>
              <S.TableHeaderCell width="8%"></S.TableHeaderCell>
              <S.TableHeaderCell width="25%">Nome</S.TableHeaderCell>
              <S.TableHeaderCell width="22%">Evento</S.TableHeaderCell>
              <S.TableHeaderCell width="35%">Descrição</S.TableHeaderCell>
              <S.TableHeaderCell width="10%"></S.TableHeaderCell>
            </S.TableHeader>
            <S.TableBody>
              {automations.length === 0 && !isLoading ? (
                <S.EmptyState>
                  Nenhuma automação cadastrada
                </S.EmptyState>
              ) : (
                automations.map((automation) => (
                  <S.TableRow key={automation.id}>
                    <S.TableCell>
                      <SwitchToggle 
                        active={automation.isActive}
                        onClick={() => handleToggleActive(automation.id, automation.isActive)}
                      >
                        <SwitchThumb active={automation.isActive}>
                          {automation.isActive && <Check size={12} />}
                        </SwitchThumb>
                      </SwitchToggle>
                    </S.TableCell>
                    <S.TableCell>{automation.name}</S.TableCell>
                    <S.TableCell>{getEventDisplayName(automation)}</S.TableCell>
                    <S.TableCell>{formatAutomationDescription(automation.description)}</S.TableCell>
                    <S.TableCell>
                      <S.ActionButtons>
                        <S.ActionButton onClick={() => handleEdit(automation.id)}>
                          <Pencil size={18} />
                        </S.ActionButton>
                        <S.ActionButton onClick={() => handleDelete(automation.id)}>
                          <Trash2 size={18} />
                        </S.ActionButton>
                      </S.ActionButtons>
                    </S.TableCell>
                  </S.TableRow>
                ))
              )}
            </S.TableBody>
          </S.Table>
        )}

        {activeAutomationTab === 'connections' && (
          <S.EmptyState>Conexões em desenvolvimento</S.EmptyState>
        )}

        {activeAutomationTab === 'integration' && (
          <S.EmptyState>Integração em desenvolvimento</S.EmptyState>
        )}

        {activeAutomationTab === 'documentation' && (
          <S.EmptyState>Documentação em desenvolvimento</S.EmptyState>
        )}
      </S.Content>
    </S.Container>
  )
}

export default AutomationsPage
